<template>
  <div class="fill-height">
    <v-container fluid class="container-main">
      <v-row v-if="loading" justify="center" class="pt-8">
        <v-col cols="12" sm="10" md="8" lg="6">
          <div class="text-center pa-8">
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
            ></v-progress-circular>
            <p class="text-body-1 mt-4 text--secondary">Loading your profile...</p>
          </div>
        </v-col>
      </v-row>

      <template v-else>
        <v-row justify="center">
          <v-col cols="12" sm="10" md="8" lg="6">
            <div class="text-center mb-6">
              <h1 class="text-h4 mb-2">{{ welcomeMessage }}</h1>
              <p class="text-body-1 text--secondary">{{ subtitle }}</p>
            </div>

            <v-card
              class="pa-5 mb-4 primary"
              dark
              @click="nav('/SmallWinsCapture')"
              style="cursor: pointer;"
              elevation="4"
            >
              <div class="d-flex align-center">
                <v-avatar color="white" size="56" class="mr-4">
                  <v-icon color="primary" large>mdi-trophy</v-icon>
                </v-avatar>
                <div class="flex-grow-1">
                  <div class="text-h6">Capture a Win</div>
                  <div class="text-body-2 opacity-80">
                    Log an achievement, new skill, or project note
                  </div>
                </div>
                <v-icon large>mdi-chevron-right</v-icon>
              </div>
            </v-card>

            <v-card class="pa-4 mb-4" flat v-if="completeness">
              <div class="d-flex align-center justify-space-between mb-3">
                <h3 class="text-subtitle-1 mb-0">Profile Strength</h3>
                <span class="text-h6 primary--text">{{ completeness.percentage }}%</span>
              </div>
              <v-progress-linear
                :value="completeness.percentage"
                color="primary"
                height="10"
                rounded
              ></v-progress-linear>
              <div class="d-flex flex-wrap mt-3">
                <v-chip
                  v-for="item in incompleteItems"
                  :key="item.item"
                  small
                  outlined
                  color="warning"
                  class="mr-1 mb-1"
                  @click="navigateToSection(item.item)"
                >
                  <v-icon x-small left>mdi-plus</v-icon>
                  Add {{ item.item }}
                </v-chip>
              </div>
            </v-card>

            <v-row>
              <v-col cols="6">
                <v-card
                  class="pa-4 text-center"
                  outlined
                  @click="nav('/Review')"
                  style="cursor: pointer;"
                >
                  <v-icon color="primary" size="32" class="mb-2">mdi-file-document</v-icon>
                  <div class="text-body-2 font-weight-medium">View Resume</div>
                </v-card>
              </v-col>
              <v-col cols="6">
                <v-card
                  class="pa-4 text-center"
                  outlined
                  @click="nav('/CareerProfile')"
                  style="cursor: pointer;"
                >
                  <v-icon color="secondary" size="32" class="mb-2">mdi-account-circle</v-icon>
                  <div class="text-body-2 font-weight-medium">Career Profile</div>
                </v-card>
              </v-col>
            </v-row>

            <v-card class="pa-4 mt-4" v-if="recentWins.length > 0">
              <div class="d-flex align-center justify-space-between mb-3">
                <h3 class="text-subtitle-1 mb-0">Recent Captures</h3>
                <v-btn x-small text color="primary" @click="nav('/SmallWinsProjects')">
                  View All
                </v-btn>
              </div>
              <v-list dense class="py-0">
                <v-list-item
                  v-for="win in recentWins.slice(0, 3)"
                  :key="win.id"
                  class="px-0 recent-win-item"
                  role="button"
                  tabindex="0"
                  @click="openWin(win)"
                  @keyup.enter="openWin(win)"
                >
                  <v-list-item-icon class="mr-2 my-2">
                    <v-icon small :color="getWinColor(win.parsedType)">
                      {{ getWinIcon(win.parsedType) }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">
                      {{ truncate(win.rawInput, 60) }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ formatDate(win.createdAt) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card>

            <div class="text-center mt-6">
              <v-btn text small color="primary" @click="nav('/Sage')" class="mr-2">
                <v-icon small class="mr-1">mdi-microphone</v-icon>
                Voice Capture
              </v-btn>
              <v-btn text small color="primary" @click="nav('/Information')">
                <v-icon small class="mr-1">mdi-pencil</v-icon>
                Edit Manually
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </template>
    </v-container>
  </div>
</template>

<script>
import { GetProfileContext } from "@/api/ProfileContextApi";
import { mapFields } from "vuex-map-fields";

export default {
  name: "CaptureWelcome",
  data() {
    return {
      loading: true,
      displayName: "",
      completeness: null,
      recentWins: [],
      daysSinceVisit: 0
    };
  },
  computed: {
    ...mapFields(["resumeId"]),
    welcomeMessage() {
      if (this.daysSinceVisit === 0) {
        return `Welcome back${this.displayName ? ', ' + this.firstName : ''}!`;
      } else if (this.daysSinceVisit === 1) {
        return `Good to see you again${this.displayName ? ', ' + this.firstName : ''}!`;
      } else if (this.daysSinceVisit <= 7) {
        return `Welcome back${this.displayName ? ', ' + this.firstName : ''}!`;
      } else {
        return `It's been a while${this.displayName ? ', ' + this.firstName : ''}!`;
      }
    },
    firstName() {
      if (!this.displayName) return "";
      return this.displayName.split(" ")[0];
    },
    subtitle() {
      if (this.daysSinceVisit > 7) {
        return "Let's catch up on what you've accomplished recently.";
      }
      return "What have you accomplished since your last visit?";
    },
    incompleteItems() {
      if (!this.completeness || !this.completeness.items) return [];
      return this.completeness.items.filter(item => !item.complete);
    }
  },
  async mounted() {
    this.initVisitTracking();
    await this.loadProfile();
  },
  methods: {
    initVisitTracking() {
      const lastVisit = localStorage.getItem("resumation_last_visit");
      if (lastVisit) {
        const lastDate = new Date(parseInt(lastVisit));
        const now = new Date();
        const diffTime = Math.abs(now - lastDate);
        this.daysSinceVisit = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      }
      localStorage.setItem("resumation_last_visit", Date.now().toString());
    },
    async loadProfile() {
      try {
        const result = await GetProfileContext(this.resumeId);
        if (result.success && result.data) {
          this.displayName = result.data.displayName || "";
          this.completeness = result.data.completeness;
          this.recentWins = result.data.smallWins || [];
        }
      } catch (e) {
        console.error("CaptureWelcome load error:", e);
      } finally {
        this.loading = false;
      }
    },
    navigateToSection(sectionName) {
      const routes = {
        "Name": "/information",
        "Contact Info": "/information",
        "Skills": "/hardskills",
        "Work Experience": "/Work",
        "Education": "/education",
        "Achievements": "/SmallWinsCapture"
      };
      this.nav(routes[sectionName] || "/information");
    },
    getWinIcon(type) {
      const icons = {
        achievement: "mdi-trophy",
        skill: "mdi-star",
        note: "mdi-note-text",
        project: "mdi-folder"
      };
      return icons[type] || "mdi-note-text";
    },
    getWinColor(type) {
      const colors = {
        achievement: "success",
        skill: "info",
        note: "grey",
        project: "warning"
      };
      return colors[type] || "grey";
    },
    truncate(text, length) {
      if (!text) return "";
      return text.length > length ? text.substring(0, length) + "..." : text;
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "";
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return "Today";
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return `${diffDays} days ago`;
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    },
    openWin(win) {
      if (!win || !win.id) return;
      this.$router.push({ path: "/SmallWinsProjects", query: { edit: win.id } });
    },
    nav(route) {
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
.opacity-80 {
  opacity: 0.8;
}

.recent-win-item {
  cursor: pointer;
}
</style>
